'use client';

import React from 'react';
import { User, X, ShieldCheck, Layers, Trash2, Calendar, Mail, Fingerprint, Award, CheckCircle2, AlertOctagon } from 'lucide-react';
import { UserItem } from '../../types';

interface InspectUserModalProps {
  user: UserItem | null;
  onClose: () => void;
  handleToggleUserStatus: (userId: string, currentStatus: string) => void;
  setUserToDelete: (user: { id: string; name: string; email: string }) => void;
  handleOpenWorkspaceBoards: (wsId: string) => void;
}

export const InspectUserModal: React.FC<InspectUserModalProps> = ({
  user,
  onClose,
  handleToggleUserStatus,
  setUserToDelete,
  handleOpenWorkspaceBoards,
}) => {
  if (!user) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-[#0D1122] border border-slate-800 rounded-3xl max-w-2xl w-full p-6 space-y-6 shadow-2xl relative max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-indigo-500/15 border border-indigo-500/30 text-indigo-400 flex items-center justify-center text-lg font-black uppercase">
              {user.name?.[0] || 'U'}
            </div>
            <div>
              <h3 className="text-base font-black text-white flex items-center gap-2">
                <span>{user.name}</span>
                {user.isSuperadmin && (
                  <span className="px-2 py-0.5 bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 rounded text-[10px] font-black uppercase">
                    SUPERADMIN
                  </span>
                )}
              </h3>
              <p className="text-xs text-slate-400 font-mono flex items-center gap-1">
                <Mail className="w-3 h-3 text-slate-500" />
                <span>{user.email}</span>
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1.5 hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="flex-1 overflow-y-auto pr-1 space-y-5">
          {/* Status Highlights Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-[#060811] border border-slate-800/80 rounded-2xl p-3 space-y-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Account Status</span>
              <span
                className={`inline-flex items-center gap-1 text-xs font-black px-2 py-0.5 rounded-lg ${
                  user.accountStatus === 'ACTIVE'
                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                    : user.accountStatus === 'SUSPENDED'
                    ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                    : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                }`}
              >
                {user.accountStatus === 'ACTIVE' ? <CheckCircle2 className="w-3 h-3" /> : <AlertOctagon className="w-3 h-3" />}
                {user.accountStatus}
              </span>
            </div>

            <div className="bg-[#060811] border border-slate-800/80 rounded-2xl p-3 space-y-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">User Type</span>
              <span className="text-xs font-bold text-indigo-300 font-mono bg-indigo-500/10 px-2 py-0.5 rounded border border-indigo-500/20 inline-block">
                {user.userType}
              </span>
            </div>

            <div className="bg-[#060811] border border-slate-800/80 rounded-2xl p-3 space-y-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">KYC Status</span>
              <span className="text-xs font-semibold text-slate-200 block">{user.kycStatus || 'NOT_SUBMITTED'}</span>
            </div>

            <div className="bg-[#060811] border border-slate-800/80 rounded-2xl p-3 space-y-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Joined Date</span>
              <span className="text-xs font-mono text-slate-300 block">
                {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>

          {/* User Details Details Section */}
          <div className="bg-[#060811] border border-slate-800/80 rounded-2xl p-4 space-y-3">
            <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
              <Fingerprint className="w-4 h-4 text-purple-400" />
              <span>System Identifiers</span>
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="bg-[#0A0D1A] p-2.5 rounded-xl border border-slate-800">
                <span className="text-slate-500 text-[10px] block">User ID</span>
                <span className="text-slate-200 font-mono text-[11px] select-all">{user.id}</span>
              </div>
              <div className="bg-[#0A0D1A] p-2.5 rounded-xl border border-slate-800">
                <span className="text-slate-500 text-[10px] block">Role Privilege</span>
                <span className="text-purple-300 font-bold text-[11px]">{user.isSuperadmin ? 'SUPERADMIN (GLOBAL OWNER)' : 'STANDARD USER'}</span>
              </div>
            </div>
          </div>

          {/* Workspaces List */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
              <Layers className="w-4 h-4 text-indigo-400" />
              <span>Associated Workspaces ({user.workspaces?.length || 0})</span>
            </h4>

            {!user.workspaces || user.workspaces.length === 0 ? (
              <div className="p-4 bg-[#060811] border border-dashed border-slate-800 rounded-2xl text-center text-xs text-slate-500">
                No active workspace memberships assigned to this user.
              </div>
            ) : (
              <div className="space-y-2">
                {user.workspaces.map((ws) => (
                  <div
                    key={ws.id}
                    onClick={() => {
                      onClose();
                      handleOpenWorkspaceBoards(ws.id);
                    }}
                    className="bg-[#060811] border border-slate-800 hover:border-purple-500/50 rounded-xl p-3 flex items-center justify-between transition-all cursor-pointer group"
                  >
                    <div>
                      <h5 className="text-xs font-bold text-white group-hover:text-indigo-300 transition-colors">{ws.name}</h5>
                      <span className="text-[10px] font-mono text-purple-400 font-semibold">Role: {ws.role}</span>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                        handleOpenWorkspaceBoards(ws.id);
                      }}
                      className="px-3 py-1.5 bg-purple-500/10 hover:bg-purple-500/25 border border-purple-500/30 text-purple-300 font-bold rounded-lg text-[10px] flex items-center gap-1 transition-all"
                    >
                      <span>Inspect Boards &rarr;</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions Footer */}
        <div className="pt-4 border-t border-slate-800 flex items-center justify-between gap-3">
          {!user.isSuperadmin && user.email !== 'admin@cognoflux.com' ? (
            <button
              onClick={() => {
                onClose();
                setUserToDelete({ id: user.id, name: user.name, email: user.email });
              }}
              className="px-3.5 py-2 bg-rose-600/20 hover:bg-rose-600/30 border border-rose-500/40 text-rose-300 font-bold text-xs rounded-xl flex items-center gap-1.5 transition-all cursor-pointer"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete Account</span>
            </button>
          ) : (
            <div></div>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                handleToggleUserStatus(user.id, user.accountStatus);
                onClose();
              }}
              className={`px-4 py-2 text-xs font-black rounded-xl transition-all cursor-pointer ${
                user.accountStatus === 'ACTIVE'
                  ? 'bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-400'
                  : 'bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400'
              }`}
            >
              {user.accountStatus === 'ACTIVE' ? 'Suspend Account' : 'Activate Account'}
            </button>

            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs rounded-xl transition-all cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
